/**
 * @zendir/ui - Tabs Component
 *
 * Tab navigation following Astro UX Design System.
 *
 * @example
 * ```tsx
 * <Tabs value={activeTab} onChange={setActiveTab}>
 *   <Tabs.List>
 *     <Tabs.Tab value="overview">Overview</Tabs.Tab>
 *     <Tabs.Tab value="telemetry">Telemetry</Tabs.Tab>
 *     <Tabs.Tab value="commands">Commands</Tabs.Tab>
 *   </Tabs.List>
 *   <Tabs.Panel value="overview">Overview content</Tabs.Panel>
 *   <Tabs.Panel value="telemetry">Telemetry content</Tabs.Panel>
 *   <Tabs.Panel value="commands">Commands content</Tabs.Panel>
 * </Tabs>
 * ```
 */

import React, { memo, createContext, useContext, useId } from "react";
import { useTheme } from "../../theme";
import { classNames, safeAccentText } from "../../utils";

interface TabsContextValue {
  value: string;
  onChange: (value: string) => void;
  baseId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("Tabs components must be used within a Tabs provider");
  }
  return context;
};

export interface TabsProps {
  /** Active tab value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Children */
  children: React.ReactNode;
  /** Custom className */
  className?: string;
}

export interface TabsListProps {
  /** Children (Tab components) */
  children: React.ReactNode;
  /** Custom className */
  className?: string;
}

export interface TabProps {
  /** Tab value */
  value: string;
  /** Disabled state */
  disabled?: boolean;
  /** Icon before label */
  icon?: React.ReactNode;
  /** Children (label) */
  children: React.ReactNode;
  /** Custom className */
  className?: string;
}

export interface TabsPanelProps {
  /** Panel value (must match Tab value) */
  value: string;
  /** Children */
  children: React.ReactNode;
  /** Custom className */
  className?: string;
}

const TabsList = memo(function TabsList({
  children,
  className = "",
}: TabsListProps): React.ReactElement {
  const { tokens } = useTheme();

  return (
    <div
      role="tablist"
      className={classNames("zendir-tabs-list", className)}
      style={{
        display: "flex",
        gap: tokens.spacing.xs,
        borderBottom: tokens.borders.divider,
        marginBottom: tokens.spacing.md,
      }}
    >
      {children}
    </div>
  );
});

const Tab = memo(function Tab({
  value,
  disabled = false,
  icon,
  children,
  className = "",
}: TabProps): React.ReactElement {
  const { tokens } = useTheme();
  const { value: activeValue, onChange, baseId } = useTabsContext();

  const isActive = value === activeValue;

  return (
    <button
      type="button"
      role="tab"
      id={`${baseId}-tab-${value}`}
      aria-controls={`${baseId}-panel-${value}`}
      aria-selected={isActive}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={() => !disabled && onChange(value)}
      className={classNames(
        "zendir-tab",
        isActive && "zendir-tab--active",
        className,
      )}
      style={{
        display: "flex",
        alignItems: "center",
        gap: tokens.spacing.xs,
        padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
        fontSize: tokens.typography.fontSize.sm,
        fontWeight: isActive
          ? tokens.typography.fontWeight.semibold
          : tokens.typography.fontWeight.normal,
        fontFamily: tokens.typography.fontFamily.primary,
        color: isActive
          ? safeAccentText(tokens.colors.accent.primary)
          : tokens.colors.text.secondary,
        backgroundColor: "transparent",
        border: "none",
        borderBottom: `${tokens.borders.width.thick} solid ${isActive ? tokens.colors.accent.primary : "transparent"}`,
        marginBottom: "-1px",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: `all ${tokens.animation.fast}`,
        outline: "none",
      }}
      onMouseEnter={(e) => {
        if (!disabled && !isActive) {
          e.currentTarget.style.color = tokens.colors.text.primary;
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled && !isActive) {
          e.currentTarget.style.color = tokens.colors.text.secondary;
        }
      }}
      onFocus={(e) => {
        e.currentTarget.style.boxShadow = tokens.borders.focusRing.subtle;
      }}
      onBlur={(e) => {
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {icon}
      {children}
    </button>
  );
});

const TabsPanel = memo(function TabsPanel({
  value,
  children,
  className = "",
}: TabsPanelProps): React.ReactElement | null {
  const { value: activeValue, baseId } = useTabsContext();

  if (value !== activeValue) return null;

  return (
    <div
      role="tabpanel"
      id={`${baseId}-panel-${value}`}
      aria-labelledby={`${baseId}-tab-${value}`}
      className={classNames("zendir-tabs-panel", className)}
    >
      {children}
    </div>
  );
});

export const Tabs = memo(function Tabs({
  value,
  onChange,
  children,
  className = "",
}: TabsProps): React.ReactElement {
  const baseId = useId();
  return (
    <TabsContext.Provider value={{ value, onChange, baseId }}>
      <div className={classNames("zendir-tabs", className)}>{children}</div>
    </TabsContext.Provider>
  );
}) as React.NamedExoticComponent<TabsProps> & {
  List: typeof TabsList;
  Tab: typeof Tab;
  Panel: typeof TabsPanel;
};

// Attach subcomponents
(Tabs as any).List = TabsList;
(Tabs as any).Tab = Tab;
(Tabs as any).Panel = TabsPanel;

export default Tabs;
